import React, { useEffect, useState, useMemo, useCallback } from "react";

const CardComponent = ({ 
  item, 
  containerWidth = 400, 
  selectedBankIds, 
  selectedBranchIds,
  savedQueries 
}) => {
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

console.log("card-savedQueries",savedQueries)

  // Get styles from cardStyles
  const styleMap = item?.cardStyles || {};
  const fieldsConfig = item?.fieldsConfig || [];

  // Memoized style function
  const getStyleForKey = useCallback((key) => {
    const color = styleMap[`${key}_color`] || "#374151";
    const fontSize = styleMap[`${key}_fontSize`] || 12;
    const textAlignment = styleMap[`${key}_textAlignment`] || "left";
    const isIdField = key.startsWith("id_");

    return {
      color,
      fontSize: `${fontSize}px`,
      fontWeight: isIdField ? "bold" : "normal",
      backgroundColor: isIdField ? "#FEE2E2" : "transparent",
      padding: isIdField ? "4px 6px" : "2px 0",
      borderRadius: isIdField ? "4px" : "0",
      textAlign: textAlignment,
    };
  }, [styleMap]);

  const isNumber = useCallback((value) => {
    return !isNaN(value) && !isNaN(parseFloat(value));
  }, []);

  const formatValue = useCallback((key, value) => {
    if (key.toLowerCase().includes("percentage") ||
        key.toLowerCase().includes("percent")) {
      return `${value}%`;
    }
    return value;
  }, []);

  // Function to check if a field should be excluded from display
  const shouldExcludeField = useCallback((key) => {
    const excludedFields = [
      "bank_id", "bankId", "Bank_ID",
      "branch_id", "branchId", "Branch_ID",
      "branchdetails_id",
    ];
    return excludedFields.includes(key);
  }, []);

  // Function to check if a field is enabled in fieldsConfig
  const isFieldEnabled = useCallback((key) => {
    if (!fieldsConfig || fieldsConfig.length === 0) {
      return true;
    }

    const fieldConfig = fieldsConfig.find(
      (config) => config.fieldName === key
    );

    if (!fieldConfig) {
      return true;
    }

    return fieldConfig.enabled;
  }, [fieldsConfig]);

  const renderPercentage = useCallback((key, value) => {
    const percentage = parseFloat(value);
    if (isNaN(percentage)) return value;

    const color =
      percentage >= 80
        ? "#10b981"
        : percentage >= 60
        ? "#f59e0b"
        : "#ef4444";
    
    const arrowDirection =
      percentage >= 80
        ? "M12 4l8 8h-6v8h-4v-8H4l8-8z"
        : percentage >= 60
        ? "M12 6l-8 8h5v8h6v-8h5l-8-8z"
        : "M12 21l-8-8h5V5h6v8h5l-8 8z";

    const textAlignment = styleMap[`${key}_textAlignment`] || "right";
    const justifyContent =
      textAlignment === "center"
        ? "center"
        : textAlignment === "left"
        ? "flex-start"
        : "flex-end";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          justifyContent: justifyContent,
          flexWrap: "wrap",
        }}
      >
        <svg
          width={Math.max(10, containerWidth * 0.02)}
          height={Math.max(10, containerWidth * 0.02)}
          viewBox="0 0 24 24"
          fill={color}
        >
          <path d={arrowDirection} />
        </svg>
        <span>{percentage}%</span>
      </div>
    );
  }, [styleMap, containerWidth]);

  // Memoized function to fetch filtered data from NiFi
const fetchFilteredData = useCallback(async (bankIds, branchIds) => {
  try {
    if (!item?.dataSource || !savedQueries) {
      console.log("No data source or saved queries available");
      return Array.isArray(item?.data) ? item.data : [];
    }

    const currentDashboardWidgetId = `WIDGET_${item.id}`;
    const matchedQuery = savedQueries.find(
      (q) => q.dashboardWidgetId === currentDashboardWidgetId
    );

    console.log("cardsavedQueries", savedQueries);
    console.log("cardmatchedQuery", matchedQuery);
    
    if (!matchedQuery) {
      console.log("No matching query found for data source:", item.dataSource);
      return Array.isArray(item?.data) ? item.data : [];
    }

    // Get query object - same as handleRefreshWithDateFilter
    const queryObject = typeof matchedQuery.queryBuilder === "string"
      ? JSON.parse(matchedQuery.queryBuilder)
      : matchedQuery.queryBuilder;

    // Extract table name from entity
    const entity = queryObject.entity || "";
    const tableName = entity.includes(".") ? entity.split(".")[1] : entity;
    
    // Get joins information
    const joins = queryObject.joins || [];
    const joinedTables = joins.map(join => {
      const table = join.table || join.alias || "";
      return table.includes(".") ? table.split(".")[1] : table;
    });

    // Helper function to determine filter field names
    const determineFilterFields = () => {
      let bankFilterField = null;
      let branchFilterField = null;

      console.log("Table name:", tableName);

      // FIRST CHECK: For any table that is NOT "bank" or "branch_details", use tableName.bank_id and tableName.branch_id
      if (tableName !== "bank" && tableName !== "branch_details") {
        bankFilterField = `${tableName}.bank_id`;
        branchFilterField = `${tableName}.branch_id`;
        return { bankFilterField, branchFilterField };
      }

      // If the first condition doesn't match, proceed with existing join logic
      // Check what tables are available
      const hasBankTable = tableName === "bank" || joinedTables.includes("bank");
      const hasBranchDetailsTable = tableName === "branch_details" || joinedTables.includes("branch_details");
      const hasJoins = joins.length > 0;

      console.log("Table analysis:", {
        tableName,
        joinedTables,
        hasBankTable,
        hasBranchDetailsTable,
        hasJoins
      });

      // Determine bank filter field
      if (tableName === "bank") {
        // If entity is bank table
        bankFilterField = "bank.id";
      } else if (hasBankTable && hasJoins) {
        // If joins are there and bank table is available
        bankFilterField = "bank.id";
      } else if (!hasBankTable && tableName !== "bank" && tableName !== "branch_details") {
        // For core tables (not bank or branch_details), use table.bank_id
        bankFilterField = `${tableName}.bank_id`;
      } else if (!hasBankTable) {
        // If no bank table available, use current table with bank_id
        bankFilterField = `${tableName}.bank_id`;
      }

      // Determine branch filter field
      if (tableName === "branch_details") {
        // If entity is branch_details table
        branchFilterField = "branch_details.id";
      } else if (hasBranchDetailsTable && hasJoins) {
        // If joins are there and branch_details table is available
        branchFilterField = "branch_details.id";
      } else if (!hasBranchDetailsTable && tableName !== "bank" && tableName !== "branch_details") {
        // For core tables (not bank or branch_details), use table.branch_id
        branchFilterField = `${tableName}.branch_id`;
      } else {
        // Check joined tables for branch_id field
        const tableWithBranchId = joinedTables.find(table => table !== "bank" && table !== "branch_details");
        if (tableWithBranchId) {
          branchFilterField = `${tableWithBranchId}.branch_id`;
        } else {
          branchFilterField = `${tableName}.branch_id`;
        }
      }

      return { bankFilterField, branchFilterField };
    };

    const { bankFilterField, branchFilterField } = determineFilterFields();
    
    console.log("Determined filter fields:", {
      bankFilterField,
      branchFilterField
    });

    const filters = {};

    // Add bank filter if bankIds provided and field determined
    if (bankIds && bankIds.length > 0 && bankFilterField) {
      filters[bankFilterField] = {
        operator: "IN",
        value: bankIds
      };
    }

    // Add branch filter if branchIds provided and field determined
    if (branchIds && branchIds.length > 0 && branchFilterField) {
      filters[branchFilterField] = {
        operator: "IN", 
        value: branchIds
      };
    } else if (!branchIds || branchIds.length === 0) {
      // Explicitly send null when branchIds is not available
      if (branchFilterField) {
        filters[branchFilterField] = null;
      }
    }

    const enhancedQuery = {
      ...queryObject,
      filters: {
        ...(queryObject.filters || {}),
        ...filters,
      },
    };

    console.log("Enhanced query with dynamic filters:", JSON.stringify(enhancedQuery, null, 2));

    setLoading(true);
    setError(null);
    
    const response = await fetch("/nifi/fetchQuery", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(enhancedQuery),
    });

    console.log("CARDJSON.stringify(enhancedQuery)", JSON.stringify(enhancedQuery));
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("Filtered Query Result:", result);

    if (result.status === 'error') {
      throw new Error(result.message || 'Query execution failed');
    }

    if (result.data && Array.isArray(result.data) && result.data.length > 0) {
      // Process the result data
      const availableFields = Object.keys(result.data[0] || {});
      const hiddenIdFields = ['bank_id', 'branchdetails_id', 'branch_id'];
      
      // Use existing fieldsConfig to determine enabled fields
      const configurableFields = availableFields.filter(field => 
        !hiddenIdFields.includes(field.toLowerCase())
      );

      const enabledFields = item.fieldsConfig
        ? item.fieldsConfig.filter(config => config.enabled).map(config => config.fieldName)
        : configurableFields;
      
      const idFields = availableFields.filter(field => 
        hiddenIdFields.includes(field.toLowerCase())
      );
      const allIncludedFields = [...enabledFields, ...idFields];

      // Filter the data to include only the required fields
      const filteredData = result.data.map(dataItem => {
        const filteredItem = {};
        allIncludedFields.forEach(field => {
          if (dataItem.hasOwnProperty(field)) {
            filteredItem[field] = dataItem[field];
          }
        });
        return filteredItem;
      });

      return filteredData;
    }
    
    return [];
    
  } catch (error) {
    console.error("Error fetching filtered data:", error);
    setError(`Failed to fetch filtered data: ${error.message}`);
    return Array.isArray(item?.data) ? item.data : [];
  } finally {
    setLoading(false);
  }
}, [item?.dataSource, item?.data, item?.fieldsConfig, item?.fromDate, item?.toDate, savedQueries]);

  // Memoized safe content items with enhanced validation
  const safeContentItems = useMemo(() => {
    if (!Array.isArray(contentItems)) {
      return [];
    }
    
    return contentItems.filter(entry => {
      // Multiple layers of validation
      if (!entry || 
          typeof entry !== 'object' || 
          entry instanceof Promise ||
          React.isValidElement(entry)) {
        return false;
      }
      
      // Check for Promise values within the object
      try {
        const hasPromiseValues = Object.values(entry).some(value => 
          value instanceof Promise
        );
        return !hasPromiseValues;
      } catch (error) {
        console.warn("Error validating entry:", error);
        return false;
      }
    });
  }, [contentItems]);

  // Enhanced data loading with proper Promise handling
  useEffect(() => {
    const loadData = async () => {
      // Early validation
      if (!item || !item.data || !Array.isArray(item.data)) {
        setContentItems([]);
        return;
      }

      try {
        let resultData;
        
        if (selectedBankIds?.length > 0 || selectedBranchIds?.length > 0) {
          // Properly await the async function
          resultData = await fetchFilteredData(selectedBankIds, selectedBranchIds);
        } else {
          resultData = item.data;
        }
        
        // Enhanced validation to ensure no Promises in data
        const validatedData = Array.isArray(resultData) 
          ? resultData.filter(entry => {
              // Filter out invalid entries including Promises
              if (!entry || 
                  typeof entry !== 'object' || 
                  entry instanceof Promise ||
                  React.isValidElement(entry)) {
                return false;
              }
              
              // Check for Promise values within the object
              const hasPromiseValues = Object.values(entry).some(value => 
                value instanceof Promise
              );
              
              return !hasPromiseValues;
            })
          : [];
        
        setContentItems(validatedData);
        
      } catch (error) {
        console.error("Error in loadData:", error);
        setError("Failed to load data");
        setContentItems([]);
      }
    };

    loadData();
  }, [selectedBankIds, selectedBranchIds, item, fetchFilteredData]);

  // Initialize contentItems on component mount with validation
  useEffect(() => {
    if (item?.data && 
        Array.isArray(item.data) && 
        item.data.length > 0 && 
        !selectedBankIds?.length && 
        !selectedBranchIds?.length) {
      
      // Validate initial data
      const validatedData = item.data.filter(entry => {
        if (!entry || 
            typeof entry !== 'object' || 
            entry instanceof Promise ||
            React.isValidElement(entry)) {
          return false;
        }
        
        const hasPromiseValues = Object.values(entry).some(value => 
          value instanceof Promise
        );
        
        return !hasPromiseValues;
      });
      
      setContentItems(validatedData);
    }
  }, [item]);

  console.log("item DATAAAAAAAAAAAAAAA", item);

  // NOW SAFE TO DO EARLY RETURNS AFTER ALL HOOKS ARE DEFINED

  // Early return after hooks if no valid item or data - SHOW NO DATA MESSAGE
  if (!item || !item.data || !Array.isArray(item.data) || item.data.length < 1) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "12px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
            }}
          >
            No Data Available
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div>Loading filtered data...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#ef4444",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div>Error: {error}</div>
      </div>
    );
  }

  // Show no data message if filtered out (AFTER FILTRATION)
  if (safeContentItems.length === 0 && 
      (selectedBankIds?.length > 0 || selectedBranchIds?.length > 0)) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "12px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
            }}
          >
            No Data Available
          </div>
          
        </div>
      </div>
    );
  }

  // Show no data message if no safe content items but item has data (edge case)
  if (safeContentItems.length === 0 && item?.data?.length > 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "12px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
            }}
          >
            No Data Available
          </div>
        </div>
      </div>
    );
  }

  // Main render with enhanced validation
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
        maxWidth: containerWidth,
        padding: "0 16px",
        boxSizing: "border-box",
      }}
    >
      {safeContentItems.map((entry, index) => {
        // Final validation before rendering
        if (!entry || 
            typeof entry !== 'object' || 
            entry instanceof Promise ||
            React.isValidElement(entry)) {
          console.warn(`Skipping invalid entry at index ${index}:`, entry);
          return null;
        }

        // Check for Promise values within the object
        try {
          const hasPromiseValues = Object.values(entry).some(value => 
            value instanceof Promise
          );
          
          if (hasPromiseValues) {
            console.warn(`Entry contains Promise values at index ${index}:`, entry);
            return null;
          }
        } catch (error) {
          console.warn(`Error validating entry at index ${index}:`, error);
          return null;
        }

        const allFields = Object.entries(entry)
          .filter(([key]) => !shouldExcludeField(key))
          .filter(([key]) => isFieldEnabled(key))
          .map(([key, value]) => ({
            key,
            value: formatValue(key, value),
            originalValue: value,
          }));

        if (allFields.length === 0) {
          return null;
        }

        const midPoint = Math.ceil(allFields.length / 2);
        const leftColumnFields = allFields.slice(0, midPoint);
        const rightColumnFields = allFields.slice(midPoint);

        return (
          <div
            key={`card-${index}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              minHeight: "24px",
              gap: `${Math.max(8, containerWidth * 0.02)}px`,
              padding: "4px 0",
            }}
          >
            {/* Left column */}
            <div
              style={{
                flex: "1",
                minWidth: "0",
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {leftColumnFields.map(({ key, value, originalValue }) => {
                const fieldStyle = getStyleForKey(key);
                return (
                  <div
                    key={`left-${key}-${index}`}
                    style={{
                      ...fieldStyle,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      hyphens: "auto",
                      lineHeight: "1.4",
                    }}
                  >
                    {key.toLowerCase().includes("percentage") ||
                    key.toLowerCase().includes("percent")
                      ? renderPercentage(key, originalValue)
                      : value}
                  </div>
                );
              })}
            </div>

            {/* Right column */}
            <div
              style={{
                flex: "1",
                minWidth: "0",
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {rightColumnFields.map(({ key, value, originalValue }) => {
                const fieldStyle = getStyleForKey(key);
                return (
                  <div
                    key={`right-${key}-${index}`}
                    style={{
                      ...fieldStyle,
                      fontWeight: isNumber(originalValue)
                        ? "600"
                        : "normal",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      hyphens: "auto",
                      lineHeight: "1.4",
                    }}
                  >
                    {key.toLowerCase().includes("percentage") ||
                    key.toLowerCase().includes("percent")
                      ? renderPercentage(key, originalValue)
                      : value}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CardComponent;